import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Grid2 as Grid, FormControl, FormGroup, InputLabel } from '@mui/material';
import { withStyles } from '@mui/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
// eslint-disable-next-line no-restricted-imports
import { useController } from 'react-hook-form-v7';
import { textFieldStyles } from '../../../Configurations/ExpenseEntryConfig';
import { COLORS, DATE_FORMATS } from '../../../../utils/Application_Constants';

const styles = (theme) => {
    const baseStyles = textFieldStyles(theme);
    return {
        datePicker: {
            width: '10em',
            ...baseStyles.root,
            '& .MuiIconButton-root': {
                color: COLORS.LT_JOURNEY,
            },
            '& .Mui-error': {
                color: COLORS.LT_PEPPER,
            },
        },
    };
};

const dateInputSlotProps = {
    popper: { sx: { zIndex: 1320 } },
    dialog: { sx: { zIndex: 1320 } },
    textField: {
        placeholder: 'mm/dd/yyyy',
        variant: 'standard',
        margin: 'none',
        InputLabelProps: { shrink: true },
        InputProps: {
            sx: { flexShrink: 1 },
            disableUnderline: true,
        },
        sx: { '& .MuiPickersInputBase-root': { mt: 1 } },
    },
    openPickerButton: { 'aria-label': 'Choose date' },
};

const ConnectedDatePicker = forwardRef(({ control, classes, name, disabled = false }, ref) => {
    const [dateError, setDateError] = useState('');
    const [focused, setFocused] = useState(false);
    const {
        field: { value, onChange: setDate },
    } = useController({ control, name, defaultValue: null });

    const handleClear = () => {
        setDate(null);
        setDateError('');
    };

    useImperativeHandle(ref, () => ({
        clear: handleClear,
    }));

    const handleDateChange = (date) => {
        if (date) {
            if (!moment(date).isValid()) {
                setDateError('Invalid date');
                return;
            }
            setDateError('');
            const newDate = moment(date).format(DATE_FORMATS.ISO_8601);
            setDate(newDate);
        } else {
            setDate(null);
            setDateError('');
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <FormControl component="fieldset" ref={ref} disabled={disabled} error={!!dateError}>
                <FormGroup>
                    <Grid container spacing={2}>
                        <Grid size={6}>
                            <InputLabel shrink focused={focused} htmlFor={name}>
                                Date
                            </InputLabel>
                            <DatePicker
                                name={name}
                                data-testid={`${name}-input`}
                                value={value ? moment(value) : null}
                                onChange={handleDateChange}
                                disabled={disabled}
                                className={classes.datePicker}
                                slotProps={{
                                    ...dateInputSlotProps,
                                    textField: {
                                        ...dateInputSlotProps.textField,
                                        id: name,
                                        onFocus: () => setFocused(true),
                                        onBlur: () => setFocused(false),
                                        helperText: dateError,
                                        error: !!dateError,
                                    },
                                    popper: dateInputSlotProps.popper,
                                }}
                            />
                        </Grid>
                    </Grid>
                </FormGroup>
            </FormControl>
        </LocalizationProvider>
    );
});

export default withStyles(styles)(ConnectedDatePicker);
